import { ArrowPathIcon, PlayIcon } from "@heroicons/react/24/outline";
import { useMutation, useQuery } from "@tanstack/react-query";
import React, { useState } from "react";
import { AgentTimeline } from "../components/AgentTimeline";
import { AgentWorkflowView } from "../components/AgentWorkflowView";
import { DebateViewer } from "../components/DebateViewer";
import { DecisionPanel } from "../components/DecisionPanel";
import { RunHistory } from "../components/RunHistory";
import { TrustPanel } from "../components/TrustPanel";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import {
  v2DownloadReport,
  v2GetRun,
  v2ListRuns,
  v2StartAnalyze,
} from "../services/tradingAgentsV2";
import type { RunState } from "../services/tradingAgentsV2";

export default function TradingAgentsAnalysis() {
  const [ticker, setTicker] = useState("AAPL");
  const [tradeDate, setTradeDate] = useState(
    () => new Date().toISOString().slice(0, 10)
  );
  const [provider, setProvider] = useState("");
  const [strategyId, setStrategyId] = useState("");
  const [activeRunId, setActiveRunId] = useState<string | null>(null);

  const runsQuery = useQuery({
    queryKey: ["v2-runs"],
    queryFn: () => v2ListRuns({ limit: 30 }),
    refetchInterval: 15000,
  });

  const detailQuery = useQuery({
    queryKey: ["v2-run", activeRunId],
    queryFn: () => v2GetRun(activeRunId!),
    enabled: Boolean(activeRunId),
    refetchInterval: (q) => {
      const s = (q.state.data as { status?: string } | undefined)?.status;
      return s === "pending" || s === "running" ? 3000 : false;
    },
  });

  const startMutation = useMutation({
    mutationFn: v2StartAnalyze,
    onSuccess: (data) => {
      setActiveRunId(data.run_id);
      runsQuery.refetch();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const t = ticker.trim().toUpperCase();
    if (!t || !tradeDate.trim()) return;
    const sid = parseInt(strategyId.trim(), 10);
    startMutation.mutate({
      ticker: t,
      trade_date: tradeDate.trim(),
      llm_provider: provider.trim() || undefined,
      strategy_id: Number.isFinite(sid) && sid > 0 ? sid : undefined,
    });
  };

  const detail = detailQuery.data;
  const state = (detail?.state ?? {}) as RunState;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-1">
          <div className="card">
            <h2 className="mb-4 text-xl font-semibold text-gray-900">
              Trading agents
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                label="Ticker"
                value={ticker}
                onChange={(e) => setTicker(e.target.value)}
                required
                placeholder="AAPL"
              />
              <Input
                label="Trade date"
                type="date"
                value={tradeDate}
                onChange={(e) => setTradeDate(e.target.value)}
                required
              />
              <div>
                <label htmlFor="llm_provider" className="label">
                  LLM provider (optional)
                </label>
                <select
                  id="llm_provider"
                  value={provider}
                  onChange={(e) => setProvider(e.target.value)}
                  className="select w-full"
                >
                  <option value="">Default</option>
                  <option value="openai">OpenAI</option>
                  <option value="anthropic">Anthropic</option>
                  <option value="google">Google GenAI</option>
                </select>
              </div>
              <Input
                label="Strategy ID (optional)"
                value={strategyId}
                onChange={(e) => setStrategyId(e.target.value)}
                placeholder="From Strategies page"
              />
              {startMutation.isError ? (
                <p className="text-sm text-danger-600">
                  {(startMutation.error as Error).message}
                </p>
              ) : null}
              <Button
                type="submit"
                className="w-full"
                isLoading={startMutation.isPending}
                leftIcon={<PlayIcon className="h-5 w-5" />}
              >
                Start analysis
              </Button>
            </form>
          </div>
        </div>

        <div className="space-y-6 lg:col-span-2">
          <div className="card">
            <h3 className="mb-3 text-lg font-semibold text-gray-900">
              Workflow & updates
            </h3>
            <AgentWorkflowView
              runId={activeRunId}
              statusLabel={detail?.status}
            />
          </div>

          <div className="card">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-lg font-semibold text-gray-900">Run detail</h3>
              {activeRunId ? (
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => detailQuery.refetch()}
                    leftIcon={<ArrowPathIcon className="h-4 w-4" />}
                  >
                    Refresh
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={async () => {
                      const blob = await v2DownloadReport(activeRunId, "html");
                      const url = URL.createObjectURL(blob);
                      window.open(url, "_blank", "noopener,noreferrer");
                    }}
                  >
                    HTML
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={async () => {
                      try {
                        const blob = await v2DownloadReport(activeRunId, "pdf");
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `agent-${activeRunId}.pdf`;
                        a.click();
                        URL.revokeObjectURL(url);
                      } catch (err) {
                        console.error(err);
                      }
                    }}
                  >
                    PDF
                  </Button>
                </div>
              ) : null}
            </div>
            <DecisionPanel
              decision={
                state.decision as {
                  action?: string;
                  confidence?: number;
                  rationale?: string;
                }
              }
              runId={activeRunId}
            />
            {state.source_credibility?.length ||
            state.contradictions?.length ||
            state.confidence_band ? (
              <div className="mt-4">
                <TrustPanel
                  credibility={state.source_credibility}
                  contradictions={state.contradictions}
                  confidenceBand={state.confidence_band}
                />
              </div>
            ) : null}
            <h4 className="mb-2 mt-4 text-sm font-semibold text-gray-800">
              Debate
            </h4>
            <DebateViewer
              debate={
                state.debate as {
                  rounds?: { agent?: string; summary?: string }[];
                  note?: string;
                }
              }
            />
            {state.agent_log?.length ? (
              <>
                <h4 className="mb-2 mt-4 text-sm font-semibold text-gray-800">
                  Agent timeline
                </h4>
                <AgentTimeline log={state.agent_log} />
              </>
            ) : null}
          </div>

          <div className="card">
            <h3 className="mb-3 text-lg font-semibold text-gray-900">
              Recent runs
            </h3>
            <RunHistory
              runs={runsQuery.data?.runs ?? []}
              selectedRunId={activeRunId}
              onSelect={(id) => setActiveRunId(id)}
              isLoading={runsQuery.isLoading}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
